/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
import { action, computed, observable, toJS } from 'mobx';

import flatMap from 'lodash/flatMap';
import merge from 'deepmerge';
import { DateTime, DateTimeUnit } from 'luxon';
import { CatalogEntry } from 'types';
import { toSlug } from 'helpers/utils';
import { PREMIUM_VT_SESSION_STATES } from 'config/constants';
import CatalogStore from 'stores/Catalog';
import FiltersStore from 'stores/common/Filters';
import PremiumSessionsStore from 'stores/PremiumSessions';
import { PremiumSession, PremiumSessionAggregate } from 'types/premiumSession';
import VocabularyStore from 'stores/Vocabulary';
import { RangeModifier } from 'react-day-picker';
import type { IRootStore } from 'stores/IRootStore';
import {
  getFilterConfigurations,
  PremiumSessionContentWithSchedule,
  RangeFilter,
} from './config';

type RangeFilters = {
  daterange?: RangeFilter<Date>;
  timerange?: RangeFilter<Date>;
};

class PremiumSessionFilterStore extends FiltersStore<PremiumSessionAggregate> {
  @observable loaded = false;

  @observable _filteredPremiumSessionAggregate: PremiumSessionAggregate[] = [];

  @observable hasAppliedFilters: boolean = false;

  @observable hideFullSessions: boolean = false;

  @observable rangeFilters: RangeFilters = {};

  // eslint-disable-next-line class-methods-use-this
  @observable filterQueryFunc: any = () => {};

  premiumSessionsStore: PremiumSessionsStore;

  catalogStore: CatalogStore;

  rootStore: IRootStore;

  constructor(
    premiumSessionsStore: PremiumSessionsStore,
    catalogStore: CatalogStore,
    rootStore: IRootStore,
  ) {
    const { checkboxFilters, rangeFilters } =
      PremiumSessionFilterStore.initializeFilters();
    super(checkboxFilters, rootStore.routerStore);
    this.rangeFilters = rangeFilters;
    this.premiumSessionsStore = premiumSessionsStore;
    this.catalogStore = catalogStore;
    this.rootStore = rootStore;
  }

  getFilterConfigs = () =>
    getFilterConfigurations(this.rootStore.uiStore?.tFunction);

  @computed get filteredEntries(): PremiumSession[] {
    // if there are no filters applied, return all the sessions
    const entryList = this.hasAppliedFilters
      ? this._filteredPremiumSessionAggregate
      : this.premiumSessionsStore.premiumSessionAggregateList;
    return flatMap(entryList, 'sessions');
  }

  @computed
  get filteredSessionContentEntries(): PremiumSessionContentWithSchedule[] {
    return this._filteredPremiumSessionAggregate.map((extended) => ({
      ...extended.content,
      scheduledSessions: extended.sessions,
    }));
  }

  @computed get filteredCount(): number {
    return this.filteredEntries.length;
  }

  @action flattenFilters = (key: string) =>
    this.filters[key]?.filter((item) => item.checked).map((item) => item.key) ??
    [];

  /**
   *  Filter the entries based on the date and time ranges.
   */
  @action filterByDateTimeRanges = (
    _filteredEntries: PremiumSession[],
  ): PremiumSession[] => {
    const { daterange } = this.rangeFilters;

    const startDateRange: DateTime = daterange?.from
      ? PremiumSessionFilterStore.getStartDate(daterange.from)
      : PremiumSessionFilterStore.getStartDate(new Date());

    // the end date might be undefined
    const endDateRange: DateTime | undefined = daterange?.to
      ? PremiumSessionFilterStore.getEndDate(daterange.to)
      : undefined;

    const { timerange } = this.rangeFilters;
    const startTimeRange: DateTime | undefined = timerange?.from
      ? DateTime.now().set({
          hour: DateTime.fromJSDate(timerange.from).hour,
          minute: DateTime.fromJSDate(timerange.from).minute,
          second: DateTime.fromJSDate(timerange.from).second,
        })
      : undefined;

    const endTimeRange: DateTime | undefined = timerange?.to
      ? DateTime.now().set({
          hour: DateTime.fromJSDate(timerange.to).hour,
          minute: DateTime.fromJSDate(timerange.to).minute,
          second: DateTime.fromJSDate(timerange.to).second,
        })
      : undefined;

    const filteredByRanges =
      this.rangeFilters.daterange.from ||
      this.rangeFilters.timerange.from ||
      this.rangeFilters.timerange.to
        ? _filteredEntries.filter((entry) => {
            const isBetweenDateRange = this.rangeFilters.daterange.from
              ? PremiumSessionFilterStore.isBetweenDateTimeRange(
                  entry.start_time,
                  startDateRange,
                  endDateRange,
                  'day',
                )
              : false;

            if (
              this.rangeFilters.timerange.from ||
              this.rangeFilters.timerange.to
            ) {
              const entryDt = DateTime.fromISO(entry.start_time);
              const sessionStartTime = DateTime.now().set({
                hour: entryDt.hour,
                minute: entryDt.minute,
                second: entryDt.second,
              });

              const isBetweenTimeRange =
                PremiumSessionFilterStore.isBetweenDateTimeRange(
                  sessionStartTime,
                  startTimeRange,
                  endTimeRange,
                );

              return this.rangeFilters.daterange.from
                ? isBetweenDateRange && isBetweenTimeRange
                : isBetweenTimeRange;
            }

            return isBetweenDateRange;
          })
        : _filteredEntries;
    return filteredByRanges;
  };

  /**
   * Filter the entries based on the current filters.
   * This applies extra filters in addition to the ones defined in the filter configurations.
   */
  @action filterEntries = () => {
    let _filteredPremiumSessionAggregate =
      this.premiumSessionsStore.premiumSessionAggregateList;

    if (this.hideFullSessions) {
      // For each PremiumSessionContent, filter out some PremiumSessions:
      _filteredPremiumSessionAggregate = _filteredPremiumSessionAggregate.map(
        (entry) => ({
          ...entry,
          sessions: entry.sessions.filter((s) => s.seats_available > 0),
        }),
      );
    }

    // then filter the entries based on the filter configs
    _filteredPremiumSessionAggregate =
      FiltersStore.getFilteredEntries<PremiumSessionAggregate>(
        this.premiumSessionsStore.premiumSessionAggregateList,
        this.getFilterConfigs(),
        this.filters,
      );

    const hasRangeFilters =
      this.rangeFilters.daterange?.from !== undefined ||
      this.rangeFilters.timerange?.from !== undefined;

    if (hasRangeFilters) {
      // extended sessions need to have at least one session within the range. If not, they will be filtered out.
      // if extended session has at least 1 session within the range, then the ones that are not within range will
      // be removed from the sessions array.
      _filteredPremiumSessionAggregate =
        _filteredPremiumSessionAggregate.filter(
          (extendedSession: PremiumSessionAggregate) => {
            const sessionScheduledState = [PREMIUM_VT_SESSION_STATES.scheduled];

            const scheduledSessions = extendedSession.sessions?.filter((s) =>
              sessionScheduledState.includes(s.state),
            );

            const sessionsWithinDateRange =
              this.filterByDateTimeRanges(scheduledSessions);

            if (Array.isArray(sessionsWithinDateRange)) {
              // here we replace the list and keep only the sessions that are within range
              extendedSession.sessions = sessionsWithinDateRange.map(
                (session) =>
                  ({
                    ...session,
                  } as PremiumSession),
              );
            }

            return extendedSession.sessions?.length;
          },
        );
    }

    this._filteredPremiumSessionAggregate = _filteredPremiumSessionAggregate;
    this.hasAppliedFilters = true;
  };

  /**
   * Get the catalog entries that have premium classes
   */
  @computed get catalogPremiumEntries(): CatalogEntry[] {
    return this.catalogStore.entries?.filter((course) => {
      const courseSlug = toSlug(course.code, course.version);
      return this.premiumSessionsStore.premiumSessionsContent?.some(
        (p) => p.offering_slug === courseSlug,
      );
    });
  }

  /**
   * Populate filters for the UI with data from the catalog
   * @param filterQueryFunc Function to filter the premium sessions before populating the filters. Useful for excluding sessions that are not visible in a specific context. Applies only to PremiumSession entity.
   */
  @action getFilterInfo = async (
    i18n,
    t,
    vocabularyStore: VocabularyStore,
    filterQueryFunc: (session: PremiumSession) => boolean = () => true,
  ) => {
    this.filterQueryFunc = filterQueryFunc;
    // ignore PremiumSessions based on the filterQueryFunc
    const filteredExtendedEntries =
      this.premiumSessionsStore.premiumSessionAggregateList.map((entry) => ({
        ...entry,
        sessions: entry.sessions.filter(filterQueryFunc),
      }));

    await this.__getFilterInfo(
      this.rootStore.uiStore.currentLanguage,
      this.rootStore.vocabularyStore,
      filteredExtendedEntries,
    );

    return this.filters;
  };

  @computed get dateRange(): RangeFilter<Date> {
    return this.rangeFilters.daterange;
  }

  @computed get timeRange() {
    return this.rangeFilters.timerange;
  }

  @action setDateRange = (range: RangeModifier) => {
    this.rangeFilters.daterange = merge(
      toJS(this.rangeFilters.daterange),
      range || {},
    );
  };

  @action setStartDate = (startDate: Date) => {
    this.rangeFilters.daterange = {
      ...this.rangeFilters.daterange,
      from: startDate,
    };
  };

  @action setEndDate = (endDate: Date) => {
    this.rangeFilters.daterange = {
      ...this.rangeFilters.daterange,
      to: endDate,
    };
  };

  @action setTimeRange = (range: RangeModifier) => {
    this.rangeFilters.timerange = merge(
      toJS(this.rangeFilters.timerange),
      range || {},
    );
  };

  @action setStartTime = (startTime: Date) => {
    this.rangeFilters.timerange = {
      ...this.rangeFilters.timerange,
      from: startTime,
    };
  };

  @action setEndTime = (endTime: Date) => {
    this.rangeFilters.timerange = {
      ...this.rangeFilters.timerange,
      to: endTime,
    };
  };

  @action resetCalendar = () => {
    if (this.rangeFilters.daterange.from) {
      this.rangeFilters.daterange.from = undefined;
    }
    if (this.rangeFilters.daterange.to) {
      this.rangeFilters.daterange.to = undefined;
    }
  };

  @action resetCalendarTime = () => {
    if (this.rangeFilters.timerange.from) {
      this.rangeFilters.timerange.from = undefined;
    }
    if (this.rangeFilters.timerange.to) {
      this.rangeFilters.timerange.to = undefined;
    }
  };

  @action
  clearFilters(exceptions: string[] = []) {
    this.resetCalendar();
    this.resetCalendarTime();
    super.clearFilters(exceptions, true);
  }

  static toDateTime = (input: DateTime | Date | string): DateTime => {
    if (input instanceof DateTime) return input;
    if (input instanceof Date) return DateTime.fromJSDate(input);
    return DateTime.fromISO(input);
  };

  static getStartDate = (startDate: DateTime | Date | string): DateTime =>
    PremiumSessionFilterStore.toDateTime(startDate).set({
      hour: 0,
      minute: 0,
      second: 0,
    });

  static getEndDate = (endDate: DateTime | Date | string): DateTime =>
    PremiumSessionFilterStore.toDateTime(endDate).set({
      hour: 23,
      minute: 59,
      second: 59,
    });

  static isBetweenDateTimeRange = (
    dateToCompare: DateTime | Date | string,
    start: DateTime | Date | string | undefined,
    end: DateTime | Date | string | undefined,
    unit?: DateTimeUnit,
  ) => {
    const { toDateTime } = PremiumSessionFilterStore;
    const dt = toDateTime(dateToCompare);

    let startsBefore = false;
    if (start) {
      const startDt = toDateTime(start);
      startsBefore = unit
        ? dt.startOf(unit) < startDt.startOf(unit)
        : dt < startDt;
    }

    let endsAfter = false;
    if (end) {
      const endDt = toDateTime(end);
      endsAfter = unit ? dt.startOf(unit) > endDt.startOf(unit) : dt > endDt;
    }

    return !(startsBefore || endsAfter);
  };

  private static initializeFilters() {
    const checkboxFilters = {
      instructors: [],
      languages: [],
      taxonomyProducts: [],
      taxonomyAudiences: [],
      taxonomyTopics: [],
      level: [],
    };

    const rangeFilters = {
      daterange: {
        from: undefined,
        to: undefined,
      },
      timerange: {
        from: undefined,
        to: undefined,
      },
    };

    return {
      checkboxFilters,
      rangeFilters,
    };
  }
}

export default PremiumSessionFilterStore;
